// The line of a room in the top bar, during a match: the code, and whether
// the other person is here. While they are away it counts the time, and after
// `ROOM_CLAIM_AFTER_MS` it offers the win. While this screen has no socket it
// says that it reconnects. A dropped socket is normal on a phone, so the line
// says no more than that. The words are plain placeholders.

import { ROOM_CLAIM_AFTER_MS } from '../constants';
import { el } from '../lib/dom';
import { otherSeat } from '../room/protocol';
import { gameStore } from '../state/game';
import { claimRoomWin, takeRoomSeat, voteRoomRematch } from '../state/room';
import { type RoomState, roomStore } from '../state/roomStore';

const TICK_MS = 1000;
const SECONDS_PER_MINUTE = 60;
const MS_PER_SECOND = 1000;

const CLAIM_LABEL = 'Claim the win';
const SIT_LABEL = 'Take the seat';
const REMATCH_LABEL = 'Rematch';
const REMATCH_WAIT_LABEL = 'Waiting';

const clock = (ms: number): string => {
  const seconds = Math.floor(ms / MS_PER_SECOND);
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  return `${minutes}:${String(seconds % SECONDS_PER_MINUTE).padStart(2, '0')}`;
};

export const createRoomBar = (): HTMLElement => {
  const code = el('span', { className: 'room-bar__code' });
  const presence = el('span', { className: 'room-lobby__presence room-bar__presence' });
  const words = el('span', { className: 'room-bar__words' });
  const claimButton = el('button', {
    className: 'button room-bar__claim',
    type: 'button',
    textContent: CLAIM_LABEL,
    onclick: claimRoomWin,
  });
  const sitButton = el('button', {
    className: 'button button--pass-through room-bar__claim',
    type: 'button',
    textContent: SIT_LABEL,
    onclick: takeRoomSeat,
  });
  // The final card can be put away, so the vote for a rematch is here too.
  const rematchButton = el('button', {
    className: 'button room-bar__claim',
    type: 'button',
    textContent: REMATCH_LABEL,
    onclick: voteRoomRematch,
  });
  const root = el('div', { className: 'room-bar', hidden: true }, [
    code,
    presence,
    words,
    claimButton,
    sitButton,
    rematchButton,
  ]);
  /** The clock time at which the other person went away, from the last word of the server. */
  let awaySince: number | null = null;
  let timer = 0;

  const draw = (): void => {
    const room: RoomState | null = roomStore.get();
    const view = room?.view ?? null;
    root.hidden = room === null || view === null || view.phase !== 'playing';
    window.clearTimeout(timer);
    if (!room || !view || root.hidden) return;
    code.textContent = room.code;
    const over = gameStore.get().phase === 'over';
    const friend = room.seat ? otherSeat(room.seat) : null;
    const away = awaySince === null ? null : Date.now() - awaySince;
    const reconnecting = room.status !== 'open';
    presence.classList.toggle('room-lobby__presence--here', !reconnecting && away === null);
    if (reconnecting) words.textContent = 'Reconnecting…';
    else if (friend === null) words.textContent = `Watching · ${view.spectators} here`;
    else if (!view.filled[friend]) words.textContent = 'Your friend left';
    else if (away !== null) words.textContent = `Your friend is away ${clock(away)}`;
    else words.textContent = 'Your friend is here';
    claimButton.hidden =
      reconnecting || friend === null || away === null || away < ROOM_CLAIM_AFTER_MS || over;
    // A spectator may take a seat whose person is away.
    const openSeat = (['host', 'guest'] as const).some((seat) => view.awayMs[seat] !== null);
    sitButton.hidden = reconnecting || friend !== null || !openSeat;
    rematchButton.hidden = !over || friend === null;
    rematchButton.disabled = room.votedRematch;
    rematchButton.textContent = room.votedRematch ? REMATCH_WAIT_LABEL : REMATCH_LABEL;
    if (away !== null) timer = window.setTimeout(draw, TICK_MS);
  };

  roomStore.subscribe((room) => {
    const friend = room?.seat ? otherSeat(room.seat) : null;
    const awayMs = friend && room?.view ? room.view.awayMs[friend] : null;
    awaySince = awayMs === null || awayMs === undefined ? null : Date.now() - awayMs;
    draw();
  });
  gameStore.subscribe(draw);
  return root;
};
